import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const variantMedia = this.#getVariantMedia(event.detail.resource);
    const source = event.detail.data.html;

    if (!source) {
      if (variantMedia) this.selectMedia(variantMedia, { animate: false });
      return;
    }

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) {
      if (variantMedia) this.selectMedia(variantMedia, { animate: false });
      return;
    }

    this.replaceWith(newMediaGallery);

    if (variantMedia) {
      this.#selectMediaAfterUpgrade(newMediaGallery, variantMedia);
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Selects media in the current gallery slideshow by Shopify media ID.
   *
   * @param {string | number | {id?: string | number, src?: string}} media - The Shopify media to select.
   * @param {Object} [options] - Options passed to the slideshow selector.
   * @param {boolean} [options.animate] - Whether the slideshow should animate.
   * @returns {boolean} True when matching media was found.
   */
  selectMedia(media, options = {}) {
    const mediaId = typeof media === 'object' ? media.id : media;
    const mediaSrc = typeof media === 'object' ? media.src : undefined;
    const mediaIdString = mediaId ? String(mediaId) : undefined;
    const slideshow = this.slideshow;
    const slides = slideshow?.refs.slides;

    if (!slideshow || !slides?.length) return false;

    const index = slides.findIndex((slide) => this.#slideMatchesMedia(slide, mediaIdString, mediaSrc));

    if (index === -1) return false;

    slideshow.setAttribute('initial-slide', String(index));
    slideshow.select(index, undefined, options);

    return true;
  }

  /**
   * Selects variant media once a newly inserted gallery has upgraded and initialized.
   *
   * @param {Element} mediaGallery - The media gallery element that was inserted into the document.
   * @param {{id?: string | number, src?: string}} media - The Shopify media to select.
   */
  #selectMediaAfterUpgrade(mediaGallery, media) {
    customElements.whenDefined('media-gallery').then(() => {
      requestAnimationFrame(() => {
        if (mediaGallery instanceof MediaGallery) {
          mediaGallery.selectMedia(media, { animate: false });
        }
      });
    });
  }

  /**
   * Gets variant media from Shopify's native featured_media or featured_image payloads.
   *
   * @param {Object | null} variant - The selected variant from the variant update event.
   * @returns {{id?: string | number, src?: string} | undefined}
   */
  #getVariantMedia(variant) {
    if (!variant) return undefined;

    const featuredMedia = variant.featured_media;
    const featuredImage = featuredMedia?.preview_image || variant.featured_image;
    const id = featuredMedia?.id || featuredImage?.id;
    const src = featuredImage?.src;

    if (!id && !src) return undefined;

    return { id, src };
  }

  /**
   * Checks whether a slideshow slide represents the requested media.
   *
   * @param {Element} slide - The slideshow slide to inspect.
   * @param {string | undefined} mediaId - The Shopify media ID to match.
   * @param {string | undefined} mediaSrc - The Shopify image URL to match.
   * @returns {boolean}
   */
  #slideMatchesMedia(slide, mediaId, mediaSrc) {
    if (mediaId && slide.getAttribute('slide-id') === mediaId) return true;
    if (!mediaSrc) return false;

    const image = slide.querySelector('img');
    const imageSrc = image instanceof HTMLImageElement ? image.currentSrc || image.src : undefined;

    return this.#normalizeImageSrc(imageSrc) === this.#normalizeImageSrc(mediaSrc);
  }

  /**
   * Normalizes Shopify image URLs so responsive width parameters do not prevent matches.
   *
   * @param {string | undefined} src - The image URL to normalize.
   * @returns {string | undefined}
   */
  #normalizeImageSrc(src) {
    if (!src) return undefined;

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.delete('width');
      const query = url.searchParams.toString();
      return query ? `${url.pathname}?${query}` : url.pathname;
    } catch {
      return src.split('&width=')[0].split('?width=')[0];
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
